import React, { FC, ReactNode, useEffect, useState } from "react";

import "./index.scss";

interface AvatarImageProps {
  /** A photo URL, or `null`/`undefined` for no photo -- renders `fallback` immediately either way,
   *  with no `<img>` in the DOM at all. */
  imageUrl?: string | null;
  /** Rendered in place of the photo when there is no URL, or once the photo fails to load. Callers
   *  pass their own already-built initials markup, so the no-photo DOM and classes are exactly what
   *  they were before this component existed. */
  fallback: ReactNode;
}

/**
 * google-profile-pictures-plan.md Phase 2 step 2/4: the one image-with-fallback mechanism shared by
 * `InitialsAvatar` and the three hand-rolled initials spans (`RankRow`'s unranked "you" row,
 * `RecipientSearchPicker`'s results, `RecognitionForm`'s suggested-collaborator chips) -- each keeps
 * its own size/colour treatment, but all four render a photo the same way instead of copying the
 * image/error/reset logic four times.
 *
 * `alt=""` -- the person's name always renders alongside this element, so a screen reader would
 * otherwise announce it twice. `referrerPolicy="no-referrer"` -- Google's image host can refuse a
 * hotlinked request that carries a referrer. `loading="lazy"` defers offscreen avatars, since a
 * leaderboard or table can render dozens at once. `object-fit: cover` (the shared `.avatar-image`
 * class, index.scss) fills whatever circle the caller's own CSS already sizes.
 *
 * @param props.imageUrl - the photo's URL, or `null`/`undefined` for no photo.
 * @param props.fallback - the caller's own initials markup, rendered when there is no URL or the
 *   image fails to load.
 */
const AvatarImage: FC<AvatarImageProps> = (props) => {
  const { imageUrl, fallback } = props;
  const [imageFailed, setImageFailed] = useState(false);

  // A new (or cleared) URL means a fresh photo is expected -- an earlier failure must not keep
  // hiding a URL this render hasn't even tried yet.
  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!imageUrl || imageFailed) {
    return <>{fallback}</>;
  }

  return (
    <img
      className="avatar-image"
      src={imageUrl}
      alt=""
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
};

export default AvatarImage;
